@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material.icons.rounded.*
import androidx.compose.material3.*
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.ui.draw.blur
import com.example.monetix.stripSystemMarker
import com.example.monetix.ui.components.TxLogo
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import com.example.monetix.db.*
import com.example.monetix.utils.StatsEngine
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.basicMarquee
import androidx.compose.animation.core.*
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.monetix.getSalaryDate
import com.example.monetix.ui.components.ModernMoneyText
import com.example.monetix.ui.theme.getDynamicBalanceColor
import com.example.monetix.utils.triggerHapticTick
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import kotlin.math.abs

// ==========================================
// 🚀 FORMATTATORE SOLDI MODERNO E PULIZIA
// ==========================================

fun formatKmpEuro(cents: Long): String {
    val a = abs(cents)
    val e = a / 100
    val d = (a % 100).toString().padStart(2, '0')
    val s = if (cents < 0) "-" else ""
    return "$s$e,$d €"
}

fun formatShortNumber(euros: Long): String {
    return when {
        euros >= 1_000_000 -> "${euros / 1_000_000}.${(euros % 1_000_000) / 100_000}M"
        euros >= 10_000 -> "${euros / 1_000}.${(euros % 1_000) / 100}k"
        else -> euros.toString()
    }
}

fun getMonthName(m: Int) = listOf("Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic")[m - 1]

fun formatKmpDateShort(days: Long): String {
    val date = LocalDate.fromEpochDays(days.toInt())
    return "${date.dayOfMonth.toString().padStart(2, '0')} ${getMonthName(date.monthNumber)}"
}

private fun cleanTextForUi(text: String?, transactions: List<TransactionEntity> = emptyList()): String {
    if (text.isNullOrBlank()) return ""

    val textWithoutTags = text.replace(Regex("\\[.*?\\]"), "").trim()

    if (textWithoutTags.isNotBlank() && textWithoutTags.length > 1) {
        return textWithoutTags
    }

    val tx = transactions.find { t ->
        val mk = t.merchantKey ?: ""
        val ds = t.description ?: ""
        (mk.isNotBlank() && (text.contains(mk, ignoreCase = true) || mk.contains(text, ignoreCase = true))) ||
                (ds.isNotBlank() && (text.contains(ds, ignoreCase = true) || ds.contains(text, ignoreCase = true)))
    }

    if (tx != null) {
        val descCleaned = tx.description?.replace(Regex("\\[.*?\\]"), "")?.trim()
        if (!descCleaned.isNullOrBlank() && descCleaned.uppercase() != "OPERAZIONE") {
            return descCleaned
        }

        val merchCleaned = tx.merchantKey?.replace(Regex("\\[.*?\\]"), "")?.trim()
        if (!merchCleaned.isNullOrBlank()) {
            return merchCleaned
        }

        val sub = tx.subcategoryText?.trim()
        if (!sub.isNullOrBlank()) return sub
    }

    val upper = text.uppercase()
    if (upper.contains("[CART:")) return "Cartella a Rate"
    if (upper.contains("[REC:")) return "Spesa Automatica"
    if (upper.contains("[FIN:")) return "Rata Finanziamento"
    if (upper.contains("[PB:")) return "Salvadanaio"
    if (upper.contains("[SAV:") || upper.contains("[GOAL:")) return "Obiettivo Risparmio"

    return "Operazione Automatica"
}

enum class StatCardType {
    BALANCES_OVERVIEW, BANK_RECONCILIATION, FIXED_EXPENSES, FORECAST, BALANCE, KPI, PIE, INSIGHTS, UPCOMING, HISTORY, HASHTAGS, CASH_FLOW, LIQUIDITA_IMPEGNATA
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun StatsScreen(
    db: MonetixDatabase,
    availableBalanceCents: Long,
    safetyBufferCents: Long,
    hideBalances: Boolean,
    favorites: List<FavoriteTemplateEntity>,
    latest: List<TransactionEntity>,
    categories: List<CategoryEntity>,
    activeAccountId: String?,
    recurringItems: List<RecurringExpenseEntity>,
    financingItems: List<FinancingEntity>,
    accounts: List<AccountEntity>,
    salaryDay: Int,
    decemberSalaryDay: Int,
    anticipateWeekends: Boolean,
    savingsGoals: List<SavingsGoalEntity> = emptyList(),
    piggyBanks: List<PiggyBankEntity> = emptyList(),
    bankAdjustmentCents: Long = 0L,
    onBankAdjustmentChanged: (Long) -> Unit = {},
    onCreateCategory: (String) -> Unit = {},
    onAddIncome: () -> Unit = {},
    onAddExpense: () -> Unit = {},
    onAddTransfer: () -> Unit = {},
    onSelectActiveAccount: (String?) -> Unit = {},
    onPopupStateChanged: (Boolean) -> Unit = {},
    onBack: () -> Unit = {},
    onOpenGlobalSearchResult: ((String, String?) -> Unit)? = null
) {
    val listState = rememberLazyListState()
    val scope = rememberCoroutineScope()

    val settings = remember { com.russhwolf.settings.Settings() }
    var localBankAdjustment by remember(activeAccountId) {
        mutableLongStateOf(settings.getLong("bank_adj_${activeAccountId ?: "global"}", 0L))
    }

    val isCollapsed by remember { derivedStateOf { listState.firstVisibleItemIndex > 0 || listState.firstVisibleItemScrollOffset > 20 } }

    var localHideBalances by remember(hideBalances) { mutableStateOf(hideBalances) }
    var isSearchActive by rememberSaveable { mutableStateOf(false) }
    var localSearchQuery by rememberSaveable { mutableStateOf("") }
    var showAccountSelectorSheet by rememberSaveable { mutableStateOf(false) }

    var selectedCategoryForDetails by remember { mutableStateOf<String?>(null) }
    var selectedMerchantForDetails by remember { mutableStateOf<String?>(null) }
    var activeBreakdownType by rememberSaveable { mutableStateOf<String?>(null) }
    var historyOffset by remember { mutableIntStateOf(0) }

    val categoryNameById = remember(categories) { categories.associate { it.id to it.name } }
    val activeAccountName = accounts.firstOrNull { it.id == activeAccountId }?.name ?: "Tutti i conti"

    val totalFixedMonthlyCents = remember(recurringItems, financingItems, activeAccountId) {
        val recTotal = recurringItems.filter {
            !it.isDeleted && it.isEnabled && it.type != "INCOME" && (activeAccountId == null || it.accountId == activeAccountId)
        }.sumOf { it.amountCents }

        val finTotal = financingItems.filter {
            !it.isDeleted && !it.isClosed && (activeAccountId == null || it.accountId == activeAccountId)
        }.sumOf { it.installmentAmountCents + (it.commissionCents ?: 0L) }
        recTotal + finTotal
    }

    val todayEpochDays = remember { Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong() }
    val visibleTxs = remember(latest) { latest.filter { !it.isHidden } }

    val emergencyBalanceCents = remember(latest, todayEpochDays, activeAccountId) {
        var sum = 0L
        for (tx in latest) {
            if (tx.isDeleted || tx.dateEpochDay > todayEpochDays) continue
            val isTarget = tx.accountId == "sys_emergency_fund" || tx.toAccountId == "sys_emergency_fund" || tx.fromAccountId == "sys_emergency_fund"
            if (!isTarget) continue

            if (activeAccountId == null) {
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == "sys_emergency_fund") sum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == "sys_emergency_fund") sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == "sys_emergency_fund") sum -= tx.amountCents
                        if (tx.toAccountId == "sys_emergency_fund") sum += tx.amountCents
                    }
                }
            } else {
                if (tx.type == "TRANSFER") {
                    if (tx.fromAccountId == activeAccountId && tx.toAccountId == "sys_emergency_fund") sum += tx.amountCents
                    else if (tx.fromAccountId == "sys_emergency_fund" && tx.toAccountId == activeAccountId) sum -= tx.amountCents
                }
            }
        }
        sum
    }

    var periodOffset by remember { mutableIntStateOf(0) }

    val (periodStart, periodEnd) = remember(periodOffset, salaryDay, decemberSalaryDay, anticipateWeekends) {
        val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
        val targetDate = today.plus(DatePeriod(months = periodOffset))
        val currentMonthSalary = getSalaryDate(targetDate.year, targetDate.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)

        if (targetDate < currentMonthSalary) {
            val prevMonthDate = targetDate.minus(DatePeriod(months = 1))
            val start = getSalaryDate(prevMonthDate.year, prevMonthDate.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)
            val end = currentMonthSalary.minus(DatePeriod(days = 1))
            start to end
        } else {
            val nextMonthDate = targetDate.plus(DatePeriod(months = 1))
            val nextSalary = getSalaryDate(nextMonthDate.year, nextMonthDate.monthNumber, salaryDay, decemberSalaryDay, anticipateWeekends)
            currentMonthSalary to nextSalary.minus(DatePeriod(days = 1))
        }
    }

    val periodEndEpoch = periodEnd.toEpochDays().toLong()
    val periodStartEpoch = periodStart.toEpochDays().toLong()

    fun isCashSubTx(tx: TransactionEntity): Boolean {
        val key = (tx.merchantKey ?: "").lowercase()
        val desc = (tx.description ?: "").lowercase()
        return tx.id.startsWith("tx_cash_") || tx.accountId?.startsWith("sys_cash_") == true || key.contains("[cash:") || desc.contains("[cash:")
    }

    val baseAvailable = remember(latest, activeAccountId, accounts, todayEpochDays, periodEndEpoch) {
        var sum = 0L
        val activeAccountIds = accounts.filter { !it.isDeleted }.map { it.id }.toSet()
        for (tx in latest) {
            if (tx.isDeleted || tx.dateEpochDay > periodEndEpoch) continue
            if (isCashSubTx(tx)) continue

            val isToSavingsAcc = tx.type == "TRANSFER" && tx.toAccountId != null && accounts.any { it.id == tx.toAccountId && it.isSavings }
            val isFutureSavings = tx.dateEpochDay > todayEpochDays && (
                    tx.merchantKey?.contains("[pb:", ignoreCase = true) == true || tx.merchantKey?.contains("[sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[goal:", ignoreCase = true) == true || tx.merchantKey?.contains("[pac_sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("salvadanaio", ignoreCase = true) == true || tx.description?.contains("[pb:", ignoreCase = true) == true ||
                            tx.description?.contains("[sav:", ignoreCase = true) == true || tx.description?.contains("[goal:", ignoreCase = true) == true ||
                            tx.description?.contains("[pac_sav:", ignoreCase = true) == true || tx.description?.contains("salvadanaio", ignoreCase = true) == true || isToSavingsAcc
                    )
            if (isFutureSavings) continue

            if (activeAccountId == null) {
                val isMainActive = tx.accountId in activeAccountIds
                val isFromActive = tx.fromAccountId in activeAccountIds
                val isToActive = tx.toAccountId in activeAccountIds
                when (tx.type) {
                    "INCOME" -> if (isMainActive && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    "EXPENSE" -> if (isMainActive) sum -= tx.amountCents
                    "TRANSFER" -> {
                        // 🚀 FIX: Uguale a Home e Transazioni, giroconti validi NON SCALANO in globale
                        if (isFromActive && !isToActive) sum -= tx.amountCents
                        else if (!isFromActive && isToActive && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    }
                }
            } else {
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == activeAccountId && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == activeAccountId) sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == activeAccountId) sum -= tx.amountCents
                        if (tx.toAccountId == activeAccountId && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    }
                }
            }
        }
        sum
    }

    val currentAccounting = remember(latest, activeAccountId, accounts, todayEpochDays) {
        var sum = 0L
        val activeAccountIds = accounts.filter { !it.isDeleted }.map { it.id }.toSet()
        for (tx in latest) {
            if (tx.isDeleted || tx.dateEpochDay > todayEpochDays) continue
            if (isCashSubTx(tx)) continue

            if (activeAccountId == null) {
                val isMainActive = tx.accountId in activeAccountIds
                val isFromActive = tx.fromAccountId in activeAccountIds
                val isToActive = tx.toAccountId in activeAccountIds
                when (tx.type) {
                    "INCOME" -> if (isMainActive && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    "EXPENSE" -> if (isMainActive) sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (isFromActive && !isToActive) sum -= tx.amountCents
                        else if (!isFromActive && isToActive && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    }
                }
            } else {
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == activeAccountId && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == activeAccountId) sum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == activeAccountId) sum -= tx.amountCents
                        if (tx.toAccountId == activeAccountId && tx.dateEpochDay <= todayEpochDays) sum += tx.amountCents
                    }
                }
            }
        }
        sum
    }

    val searchResults = remember(localSearchQuery, visibleTxs, categoryNameById) {
        if (localSearchQuery.isBlank()) emptyList()
        else {
            val queryStr = localSearchQuery.replace(",", ".").lowercase()
            val queryTokens = queryStr.split(" ").filter { it.isNotBlank() }
            visibleTxs.filter { tx ->
                if (tx.isDeleted) return@filter false
                val absCents = abs(tx.amountCents)
                val eur = absCents / 100
                val cent = (absCents % 100).toString().padStart(2, '0')
                val amountWithDot = "$eur.$cent"
                val amountWithComma = "$eur,$cent"
                val haystack = "${tx.description ?: ""} ${tx.merchantKey ?: ""} ${tx.subcategoryText ?: ""} ${categoryNameById[tx.categoryId] ?: ""} $amountWithDot $amountWithComma".lowercase()
                queryTokens.all { token -> haystack.contains(token) }
            }.sortedByDescending { it.dateEpochDay }
        }
    }

    val searchIncomeTotal = remember(searchResults) { searchResults.filter { it.type == "INCOME" }.sumOf { it.amountCents } }
    val searchExpenseTotal = remember(searchResults) { searchResults.filter { it.type == "EXPENSE" }.sumOf { it.amountCents } }

    val usedHashtags = remember(visibleTxs) {
        val tagRegex = Regex("#\\w+")
        visibleTxs.filter { !it.isDeleted && it.description != null }
            .flatMap { tagRegex.findAll(it.description!!).map { match -> match.value.lowercase() }.toList() }
            .groupingBy { it }.eachCount().entries.sortedByDescending { it.value }.map { it.key }
    }

    val savingsAccountIds = remember(accounts) { accounts.filter { it.isSavings }.map { it.id }.toSet() }

    val stats = remember(visibleTxs, periodStart, periodEnd, activeAccountId, recurringItems, financingItems, categories, savingsAccountIds, currentAccounting, historyOffset) {
        StatsEngine.calculateMonthlyStats(
            startDate = periodStart,
            endDate = periodEnd,
            latestTx = visibleTxs,
            categories = categories,
            recurringItems = recurringItems,
            financingItems = financingItems,
            activeAccountId = activeAccountId,
            availableBalanceCents = currentAccounting,
            savingsAccountIds = savingsAccountIds,
            historyOffsetMonths = historyOffset
        )
    }

    // LISTE ACCANTONAMENTI
    val activeSogniList = remember(savingsGoals, visibleTxs, activeAccountId, todayEpochDays, accounts) {
        savingsGoals.filter { !it.isDeleted && !it.isCompleted && !it.name.contains("[PAC:") && (activeAccountId == null || belongsToAccount(it.name, activeAccountId, accounts)) }.mapNotNull { goal ->
            val amount = visibleTxs.filter { tx ->
                !tx.isDeleted && tx.dateEpochDay <= todayEpochDays &&
                        (tx.merchantKey?.contains(":${goal.id}]") == true || tx.description?.contains(":${goal.id}]") == true) &&
                        (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId || tx.accountId == "salvadanaio_virtuale" || tx.fromAccountId == "salvadanaio_virtuale")
            }.fold(0L) { acc, tx ->
                val isDeposit = (tx.type == "EXPENSE" && tx.amountCents > 0) || (tx.type == "TRANSFER" && tx.fromAccountId != "salvadanaio_virtuale")
                val isWithdrawal = tx.type == "EXPENSE" && tx.amountCents < 0
                val isTransferOut = tx.type == "TRANSFER" && tx.fromAccountId == "salvadanaio_virtuale"
                val isIncomeUnlock = tx.type == "INCOME"
                acc + when {
                    isDeposit -> tx.amountCents
                    isWithdrawal -> tx.amountCents
                    isTransferOut -> -tx.amountCents
                    isIncomeUnlock -> -tx.amountCents
                    else -> 0L
                }
            }.coerceAtLeast(0L)
            if (amount > 0L) goal to amount else null
        }
    }

    val activePacList = remember(savingsGoals, visibleTxs, activeAccountId, todayEpochDays, accounts) {
        savingsGoals.filter { !it.isDeleted && !it.isCompleted && it.name.contains("[PAC:") && (activeAccountId == null || belongsToAccount(it.name, activeAccountId, accounts)) }.mapNotNull { pac ->
            val amount = visibleTxs.filter { tx ->
                !tx.isDeleted && tx.dateEpochDay <= todayEpochDays &&
                        (tx.merchantKey?.contains(":${pac.id}]") == true || tx.description?.contains(":${pac.id}]") == true) &&
                        (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId || tx.accountId == "salvadanaio_virtuale" || tx.fromAccountId == "salvadanaio_virtuale")
            }.fold(0L) { acc, tx ->
                val isDeposit = (tx.type == "EXPENSE" && tx.amountCents > 0) || (tx.type == "TRANSFER" && tx.fromAccountId != "salvadanaio_virtuale")
                val isWithdrawal = tx.type == "EXPENSE" && tx.amountCents < 0
                val isTransferOut = tx.type == "TRANSFER" && tx.fromAccountId == "salvadanaio_virtuale"
                val isIncomeUnlock = tx.type == "INCOME"
                acc + when {
                    isDeposit -> tx.amountCents
                    isWithdrawal -> tx.amountCents
                    isTransferOut -> -tx.amountCents
                    isIncomeUnlock -> -tx.amountCents
                    else -> 0L
                }
            }.coerceAtLeast(0L)
            if (amount > 0L) pac to amount else null
        }
    }

    val activeSalvadanaiList = remember(piggyBanks, visibleTxs, activeAccountId, todayEpochDays, accounts) {
        piggyBanks.filter { !it.isDeleted && (activeAccountId == null || belongsToAccount(it.name, activeAccountId, accounts)) }.mapNotNull { bank ->
            val amount = visibleTxs.filter { tx ->
                !tx.isDeleted && tx.dateEpochDay <= todayEpochDays &&
                        (tx.merchantKey?.contains(":${bank.id}]") == true || tx.description?.contains(":${bank.id}]") == true) &&
                        (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId || tx.accountId == "salvadanaio_virtuale" || tx.fromAccountId == "salvadanaio_virtuale")
            }.fold(0L) { acc, tx ->
                val isDeposit = (tx.type == "EXPENSE" && tx.amountCents > 0) || (tx.type == "TRANSFER" && tx.fromAccountId != "salvadanaio_virtuale")
                val isWithdrawal = tx.type == "EXPENSE" && tx.amountCents < 0
                val isTransferOut = tx.type == "TRANSFER" && tx.fromAccountId == "salvadanaio_virtuale"
                val isIncomeUnlock = tx.type == "INCOME"
                acc + when {
                    isDeposit -> tx.amountCents
                    isWithdrawal -> tx.amountCents
                    isTransferOut -> -tx.amountCents
                    isIncomeUnlock -> -tx.amountCents
                    else -> 0L
                }
            }.coerceAtLeast(0L)
            if (amount >0L) bank to amount else null
        }
    }

    val totalSogni = activeSogniList.sumOf { it.second }
    val totalPac = activePacList.sumOf { it.second }
    val totalSalvadanai = activeSalvadanaiList.sumOf { it.second }

    val activeScadenzeMeseList = remember(recurringItems, visibleTxs, activeAccountId, periodStartEpoch, periodEndEpoch, accounts, stats.unpaidFinancingList) {
        val recScadenze = recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true || rec.notes?.contains("[TAX:", ignoreCase = true) == true || rec.id.startsWith("cart_") || rec.id.startsWith("tax_")

            !rec.isDeleted && rec.isEnabled && rec.type != "INCOME" && rec.nextEpochDay <= periodEndEpoch &&
                    (activeAccountId == null || rec.accountId == activeAccountId) && !isCartella &&
                    !(activeAccountId == null && rec.type == "TRANSFER" && rec.toAccountId != null && accounts.any { it.id == rec.toAccountId && !it.isDeleted })
        }.mapNotNull { rec ->
            val hasPaidThisMonth = visibleTxs.any { tx ->
                !tx.isDeleted && tx.dateEpochDay >= periodStartEpoch &&
                        (tx.description?.contains("[rec:${rec.id}]", ignoreCase = true) == true || tx.merchantKey?.contains("[rec:${rec.id}]", ignoreCase = true) == true)
            }
            if (hasPaidThisMonth) return@mapNotNull null
            (rec.name ?: "Spesa Fissa") to rec.amountCents
        }

        val finScadenze = stats.unpaidFinancingList.filter { activeAccountId == null || it.accountId == activeAccountId }.map {
            (it.name ?: "Rata") to (it.installmentAmountCents + (it.commissionCents ?: 0L))
        }
        recScadenze + finScadenze
    }
    val unpaidFixed = activeScadenzeMeseList.sumOf { it.second }

    val activeCartelleList = remember(recurringItems, visibleTxs, activeAccountId, periodStartEpoch, periodEndEpoch) {
        recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true || rec.notes?.contains("[TAX:", ignoreCase = true) == true || rec.id.startsWith("cart_") || rec.id.startsWith("tax_")
            val matchesAccount = activeAccountId == null || rec.accountId == activeAccountId || rec.accountId == "NEW_TAX_MODE" || rec.accountId.isBlank()
            !rec.isDeleted && isCartella && matchesAccount
        }.mapNotNull { rec ->
            val hasPaidThisMonth = visibleTxs.any { tx ->
                !tx.isDeleted && tx.dateEpochDay >= periodStartEpoch &&
                        (tx.description?.contains("[rec:${rec.id}]", ignoreCase = true) == true || tx.merchantKey?.contains("[rec:${rec.id}]", ignoreCase = true) == true)
            }
            if (hasPaidThisMonth) return@mapNotNull null

            val match = Regex("\\[(?:CART|TAX):([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(rec.notes ?: "")
            val rates = match?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() } ?: emptyList()
            val firstUnpaid = rates.firstOrNull { !it.endsWith(":P", ignoreCase = true) && !it.endsWith(":X", ignoreCase = true) }
            val baseAmtStr = firstUnpaid?.split("@")?.firstOrNull() ?: firstUnpaid
            val amt = baseAmtStr?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
            if (amt > 0L) (rec.name ?: "Cartella / Tassa") to amt else null
        }
    }
    val totalCartelleMese = activeCartelleList.sumOf { it.second }
    val totalScadenzeMese = unpaidFixed + totalCartelleMese

    val activeSinkingFundsList = remember(recurringItems, visibleTxs, activeAccountId) {
        recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true || rec.notes?.contains("[TAX:", ignoreCase = true) == true || rec.id.startsWith("cart_") || rec.id.startsWith("tax_")
            !rec.isDeleted && rec.isEnabled && (rec.cycleTarget ?: 0) > 0 && rec.type != "INCOME" && !isCartella && (activeAccountId == null || rec.accountId == activeAccountId)
        }.mapNotNull { rec ->
            val target = rec.cycleTarget ?: 0
            val count = visibleTxs.count { tx ->
                !tx.isDeleted && (tx.description?.contains("[rec:${rec.id}") == true || tx.merchantKey?.contains("[rec:${rec.id}") == true) &&
                        (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId)
            }.toLong()
            val currentStep = if (count == 0L) 0L else if (count % target == 0L) target.toLong() else count % target
            val amount = currentStep * rec.amountCents
            if (amount > 0L) rec to amount else null
        }
    }
    val totalSinkingFundsCents = activeSinkingFundsList.sumOf { it.second }

    val fantasmaList = remember(visibleTxs, activeAccountId) {
        visibleTxs.filter { tx ->
            !tx.isDeleted && (tx.accountId?.startsWith("sys_ghost_") == true || tx.accountId == "sys_ghost_tracker_account") &&
                    !(tx.description?.contains("[cash:", ignoreCase = true) == true || tx.merchantKey?.contains("[cash:", ignoreCase = true) == true) &&
                    !isSavingsTx(tx) && (tx.type == "EXPENSE" || tx.type == "TRANSFER") &&
                    (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId)
        }
    }
    val futuriList = remember(visibleTxs, todayEpochDays, periodEndEpoch, activeAccountId) {
        visibleTxs.filter { tx ->
            val desc = (tx.description ?: "").lowercase()
            val key = (tx.merchantKey ?: "").lowercase()
            val isFixed = desc.contains("[rec:") || desc.contains("[fin:") || key.contains("[rec:") || key.contains("[fin:")

            !tx.isDeleted && tx.dateEpochDay > todayEpochDays && tx.dateEpochDay <= periodEndEpoch && !isFixed &&
                    !(desc.contains("[cash:") || key.contains("[cash:")) && !isSavingsTx(tx) && tx.type != "INCOME" &&
                    (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId)
        }
    }
    val totalProgrammatiCents = fantasmaList.sumOf { abs(it.amountCents) } + futuriList.sumOf { abs(it.amountCents) }

    val movimentiInArrivoCents = remember(visibleTxs, todayEpochDays, periodEndEpoch, activeAccountId, accounts) {
        visibleTxs.filter { tx ->
            val isToSavingsAcc = tx.type == "TRANSFER" && tx.toAccountId != null && accounts.any { it.id == tx.toAccountId && it.isSavings }
            val isFutureSavings = tx.dateEpochDay > todayEpochDays && (
                    tx.merchantKey?.contains("[pb:", ignoreCase = true) == true || tx.merchantKey?.contains("[sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[goal:", ignoreCase = true) == true || tx.merchantKey?.contains("[pac_sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("salvadanaio", ignoreCase = true) == true || tx.description?.contains("[pb:", ignoreCase = true) == true ||
                            tx.description?.contains("[sav:", ignoreCase = true) == true || tx.description?.contains("[goal:", ignoreCase = true) == true ||
                            tx.description?.contains("[pac_sav:", ignoreCase = true) == true || tx.description?.contains("salvadanaio", ignoreCase = true) == true || isToSavingsAcc
                    )

            !tx.isDeleted && tx.dateEpochDay > todayEpochDays && tx.dateEpochDay <= periodEndEpoch && tx.type != "INCOME" &&
                    !(activeAccountId == null && tx.type == "TRANSFER" && tx.toAccountId != null && accounts.any { it.id == tx.toAccountId && !it.isDeleted }) &&
                    !tx.accountId.orEmpty().startsWith("sys_ghost_") && !tx.fromAccountId.orEmpty().startsWith("sys_ghost_") &&
                    !isFutureSavings && !isCashSubTx(tx) &&
                    (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId)
        }.sumOf { if (it.amountCents < 0) -it.amountCents else it.amountCents }
    }

    val displayBalanceCents = baseAvailable - totalScadenzeMese
    val saldoContabileCents = displayBalanceCents + totalScadenzeMese + movimentiInArrivoCents
    val saldoVisibileBancaPuro = saldoContabileCents + totalSogni + totalPac + totalSalvadanai + totalSinkingFundsCents + emergencyBalanceCents
    val liquiditaImpegnataCents = totalSogni + totalPac + totalSalvadanai + totalSinkingFundsCents + unpaidFixed + totalCartelleMese + totalProgrammatiCents + emergencyBalanceCents
    var showLiquiditaImpegnataSheet by remember { mutableStateOf(false) }

    val futureBalanceCents = remember(latest, activeAccountId, accounts, todayEpochDays) {
        var totalSum = 0L
        val activeAccountIds = accounts.filter { !it.isDeleted }.map { it.id }.toSet()
        for (tx in latest) {
            if (tx.isDeleted) continue
            if (isCashSubTx(tx)) continue

            val isToSavingsAcc = tx.type == "TRANSFER" && tx.toAccountId != null && accounts.any { it.id == tx.toAccountId && it.isSavings }
            val isFutureSavings = tx.dateEpochDay > todayEpochDays && (
                    tx.merchantKey?.contains("[pb:", ignoreCase = true) == true || tx.merchantKey?.contains("[sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("[goal:", ignoreCase = true) == true || tx.merchantKey?.contains("[pac_sav:", ignoreCase = true) == true ||
                            tx.merchantKey?.contains("salvadanaio", ignoreCase = true) == true || tx.description?.contains("[pb:", ignoreCase = true) == true ||
                            tx.description?.contains("[sav:", ignoreCase = true) == true || tx.description?.contains("[goal:", ignoreCase = true) == true ||
                            tx.description?.contains("[pac_sav:", ignoreCase = true) == true || tx.description?.contains("salvadanaio", ignoreCase = true) == true || isToSavingsAcc
                    )
            if (isFutureSavings) continue

            if (activeAccountId == null) {
                val isMainActive = tx.accountId in activeAccountIds
                val isFromActive = tx.fromAccountId in activeAccountIds
                val isToActive = tx.toAccountId in activeAccountIds
                when (tx.type) {
                    "INCOME" -> if (isMainActive) totalSum += tx.amountCents
                    "EXPENSE" -> if (isMainActive) totalSum -= tx.amountCents
                    "TRANSFER" -> {
                        // 🚀 FIX: Coerente con Home. I giroconti non abbattono il patrimonio totale.
                        if (isFromActive && !isToActive) totalSum -= tx.amountCents
                        else if (!isFromActive && isToActive) totalSum += tx.amountCents
                    }
                }
            } else {
                when (tx.type) {
                    "INCOME" -> if (tx.accountId == activeAccountId) totalSum += tx.amountCents
                    "EXPENSE" -> if (tx.accountId == activeAccountId) totalSum -= tx.amountCents
                    "TRANSFER" -> {
                        if (tx.fromAccountId == activeAccountId) totalSum -= tx.amountCents
                        if (tx.toAccountId == activeAccountId) totalSum += tx.amountCents
                    }
                }
            }
        }
        totalSum
    }

    val saldoCongelato = remember(latest, periodStartEpoch, piggyBanks, savingsGoals, recurringItems, activeAccountId, accounts) {
        val limitEpochPopup = periodStartEpoch - 1
        var rawSaldoCongelato = 0L
        for (tx in latest) {
            if (!tx.isDeleted && tx.dateEpochDay <= limitEpochPopup) {
                if (isCashSubTx(tx)) continue
                val isMainActive = if (activeAccountId == null) tx.accountId in accounts.map {it.id} else tx.accountId == activeAccountId
                val isFromActive = if (activeAccountId == null) tx.fromAccountId in accounts.map {it.id} else tx.fromAccountId == activeAccountId
                val isToActive = if (activeAccountId == null) tx.toAccountId in accounts.map {it.id} else tx.toAccountId == activeAccountId

                when (tx.type) {
                    "INCOME" -> if (isMainActive) rawSaldoCongelato += tx.amountCents
                    "EXPENSE" -> if (isMainActive) rawSaldoCongelato -= tx.amountCents
                    "TRANSFER" -> {
                        // 🚀 FIX: Coerente con Home. I giroconti non abbattono il patrimonio totale.
                        if (isFromActive && !isToActive) rawSaldoCongelato -= tx.amountCents
                        else if (!isFromActive && isToActive) rawSaldoCongelato += tx.amountCents
                    }
                }
            }
        }

        val movimentiStorici = latest.filter { !it.isDeleted && it.dateEpochDay <= limitEpochPopup }

        val storicoSalvadanai = piggyBanks.filter { !it.isDeleted && (activeAccountId == null || belongsToAccount(it.name, activeAccountId, accounts)) }.sumOf { bank ->
            movimentiStorici.filter { it.merchantKey?.contains(":${bank.id}]") == true || it.description?.contains(":${bank.id}]") == true }.sumOf { tx ->
                val isDeposit = (tx.type == "EXPENSE" && tx.amountCents > 0) || (tx.type == "TRANSFER" && tx.fromAccountId != "salvadanaio_virtuale")
                val isWithdrawal = tx.type == "EXPENSE" && tx.amountCents < 0
                val isTransferOut = tx.type == "TRANSFER" && tx.fromAccountId == "salvadanaio_virtuale"
                val isIncomeUnlock = tx.type == "INCOME"
                when {
                    isDeposit -> tx.amountCents
                    isWithdrawal -> tx.amountCents
                    isTransferOut -> -tx.amountCents
                    isIncomeUnlock -> -tx.amountCents
                    else -> 0L
                }
            }
        }

        val storicoSogni = savingsGoals.filter { !it.isDeleted && (activeAccountId == null || belongsToAccount(it.name, activeAccountId, accounts)) }.sumOf { goal ->
            movimentiStorici.filter { it.merchantKey?.contains(":${goal.id}]") == true || it.description?.contains(":${goal.id}]") == true }.sumOf { tx ->
                val isDeposit = (tx.type == "EXPENSE" && tx.amountCents > 0) || (tx.type == "TRANSFER" && tx.fromAccountId != "salvadanaio_virtuale")
                val isWithdrawal = tx.type == "EXPENSE" && tx.amountCents < 0
                val isTransferOut = tx.type == "TRANSFER" && tx.fromAccountId == "salvadanaio_virtuale"
                val isIncomeUnlock = tx.type == "INCOME"
                when {
                    isDeposit -> tx.amountCents
                    isWithdrawal -> tx.amountCents
                    isTransferOut -> -tx.amountCents
                    isIncomeUnlock -> -tx.amountCents
                    else -> 0L
                }
            }
        }

        val storicoCicli = recurringItems.filter { rec ->
            val isCartella = rec.notes?.contains("[CART:", ignoreCase = true) == true
            !rec.isDeleted && rec.isEnabled && (rec.cycleTarget ?: 0) > 0 && !isCartella && (activeAccountId == null || rec.accountId == activeAccountId)
        }.sumOf { rec ->
            val target = rec.cycleTarget ?: 0
            val count = movimentiStorici.count { tx ->
                tx.dateEpochDay >= rec.startEpochDay && (tx.description?.contains("[rec:${rec.id}") == true || tx.merchantKey?.contains("[rec:${rec.id}") == true) &&
                        (activeAccountId == null || tx.accountId == activeAccountId || tx.fromAccountId == activeAccountId)
            }.toLong()

            val currentStep = if (count == 0L) 0L else if (count % target == 0L) target.toLong() else count % target
            currentStep * rec.amountCents
        }
        rawSaldoCongelato - storicoSogni - storicoSalvadanai - storicoCicli
    }

    val improvedForecastCents = remember(displayBalanceCents, stats.expenseCents, totalFixedMonthlyCents, totalScadenzeMese, periodStartEpoch, periodEndEpoch, todayEpochDays) {
        val paidFixedCents = maxOf(0L, totalFixedMonthlyCents - totalScadenzeMese)
        val totalSpentSoFar = abs(stats.expenseCents)
        val variableSpentCents = maxOf(0L, totalSpentSoFar - paidFixedCents)
        val daysPassed = maxOf(1L, todayEpochDays - periodStartEpoch + 1)
        val daysRemaining = maxOf(0L, periodEndEpoch - todayEpochDays)
        val dailyVariableCents = variableSpentCents / daysPassed
        val estimatedRemainingVariableCents = dailyVariableCents * daysRemaining
        displayBalanceCents - estimatedRemainingVariableCents
    }

    var cardOrder by remember {
        mutableStateOf(listOf(
            StatCardType.BALANCES_OVERVIEW,
            StatCardType.CASH_FLOW,           // 🚀 FLUSSO DI CASSA PERIODO CONTABILE
            StatCardType.LIQUIDITA_IMPEGNATA, // 🚀 LIQUIDITA' IMPEGNATA CON TAP BOTTOM SHEET
            StatCardType.PIE,
            StatCardType.INSIGHTS,
            StatCardType.FIXED_EXPENSES,
            StatCardType.UPCOMING,
            StatCardType.HASHTAGS,
            StatCardType.BANK_RECONCILIATION,
            StatCardType.KPI,
            StatCardType.FORECAST,
            StatCardType.HISTORY
        ))
    }

    fun updateAndSaveOrder(newList: List<StatCardType>) { cardOrder = newList }

    val infiniteTransition = rememberInfiniteTransition()
    val bgOffset by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1000f,
        animationSpec = infiniteRepeatable(
            animation = tween(20000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "bg_offset"
    )

    val hasCustomBg = com.example.monetix.LocalHasCustomBackground.current

    Box(
        modifier = Modifier
            .fillMaxSize()
            .then(
                if (hasCustomBg) Modifier.background(Color.Transparent)
                else Modifier.background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color(0xFF4F46E5).copy(alpha = 0.8f),
                            Color(0xFF1E1B4B),
                            Color(0xFF0F172A)
                        ),
                        startY = 0f,
                        endY = bgOffset + 1500f
                    )
                )
            )
    ) {
        Scaffold(containerColor = Color.Transparent) { paddingValues ->
            Box(modifier = Modifier.fillMaxSize()) {
                Column(modifier = Modifier.fillMaxSize().padding(paddingValues)) {

                    // 🚀 HEADER STILE REVOLUT/N26[cite: 4]
                    Surface(modifier = Modifier.fillMaxWidth().zIndex(1f).statusBarsPadding(), color = Color.Transparent) {
                        Column(modifier = Modifier.padding(start = 16.dp, end = 16.dp, top = 20.dp, bottom = 12.dp)) {

                            // Top Bar con Titolo, Occhio e Cerca
                            Row(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp), verticalAlignment = Alignment.CenterVertically) {
                                Text("Statistiche", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Black), color = Color.White)
                                Spacer(Modifier.weight(1f))
                                Row(horizontalArrangement = Arrangement.spacedBy(16.dp), verticalAlignment = Alignment.CenterVertically) {
                                    IconButton(
                                        onClick = {
                                            triggerHapticTick()
                                            localHideBalances = !localHideBalances
                                        },
                                        modifier = Modifier.size(36.dp).background(if (localHideBalances) Color.White.copy(alpha = 0.22f) else Color.White.copy(alpha = 0.08f), CircleShape)
                                    ) {
                                        Icon(if (localHideBalances) Icons.Outlined.VisibilityOff else Icons.Outlined.Visibility, contentDescription = "Oscura importi", tint = Color.White, modifier = Modifier.size(18.dp))
                                    }

                                    IconButton(
                                        onClick = { isSearchActive = !isSearchActive; if (!isSearchActive) localSearchQuery = "" },
                                        modifier = Modifier.size(36.dp).background(if (isSearchActive) Color.White.copy(alpha=0.22f) else Color.White.copy(alpha=0.08f), CircleShape)
                                    ) {
                                        Icon(if (isSearchActive) Icons.Default.Close else Icons.Rounded.Search, null, tint = Color.White, modifier = Modifier.size(18.dp))
                                    }
                                }
                            }

                            // 🚀 Selettori Conto e Periodo
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    modifier = Modifier
                                        .clip(RoundedCornerShape(12.dp))
                                        .clickable { showAccountSelectorSheet = true }
                                        .padding(vertical = 4.dp)
                                ) {
                                    Text((stripSystemMarker(activeAccountName) ?: activeAccountName), style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                    Spacer(Modifier.width(4.dp))
                                    Icon(Icons.Default.KeyboardArrowDown, null, tint = Color.White.copy(alpha=0.8f), modifier = Modifier.size(20.dp))
                                }

                                // Selettore Periodo
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    IconButton(onClick = { periodOffset -= 1 }, modifier = Modifier.size(24.dp)) { Icon(Icons.Default.ChevronLeft, null, tint = Color.White.copy(alpha=0.7f)) }
                                    Text(
                                        text = if(periodOffset == 0) "Questo mese" else "${formatKmpDateShort(periodStart.toEpochDays().toLong())}",
                                        style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold),
                                        color = Color.White.copy(alpha = 0.8f)
                                    )
                                    IconButton(onClick = { periodOffset += 1 }, modifier = Modifier.size(24.dp)) { Icon(Icons.Default.ChevronRight, null, tint = Color.White.copy(alpha=0.7f)) }
                                }
                            }

                            // 🚀 AZIONI RAPIDE TRANSAZIONI
                            Spacer(Modifier.height(12.dp))
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(10.dp)
                            ) {
                                Surface(
                                    onClick = { onAddExpense() },
                                    shape = RoundedCornerShape(14.dp),
                                    color = Color.White.copy(alpha = 0.12f),
                                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                                    modifier = Modifier.weight(1f).height(44.dp)
                                ) {
                                    Row(
                                        horizontalArrangement = Arrangement.Center,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(Icons.Rounded.ArrowUpward, contentDescription = "Spesa", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(18.dp))
                                        Spacer(Modifier.width(6.dp))
                                        Text("Spesa", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                    }
                                }

                                Surface(
                                    onClick = { onAddIncome() },
                                    shape = RoundedCornerShape(14.dp),
                                    color = Color.White.copy(alpha = 0.12f),
                                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                                    modifier = Modifier.weight(1f).height(44.dp)
                                ) {
                                    Row(
                                        horizontalArrangement = Arrangement.Center,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(Icons.Rounded.ArrowDownward, contentDescription = "Entrata", tint = Color(0xFF10B981), modifier = Modifier.size(18.dp))
                                        Spacer(Modifier.width(6.dp))
                                        Text("Entrata", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                    }
                                }

                                Surface(
                                    onClick = { onAddTransfer() },
                                    shape = RoundedCornerShape(14.dp),
                                    color = Color.White.copy(alpha = 0.12f),
                                    border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                                    modifier = Modifier.weight(1f).height(44.dp)
                                ) {
                                    Row(
                                        horizontalArrangement = Arrangement.Center,
                                        verticalAlignment = Alignment.CenterVertically
                                    ) {
                                        Icon(Icons.Rounded.SwapHoriz, contentDescription = "Giroconto", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
                                        Spacer(Modifier.width(6.dp))
                                        Text("Giroconto", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                    }
                                }
                            }

                            // Barra di ricerca espandibile
                            AnimatedVisibility(visible = isSearchActive, enter = expandVertically(), exit = shrinkVertically()) {
                                OutlinedTextField(
                                    value = localSearchQuery, onValueChange = { localSearchQuery = it },
                                    modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                                    placeholder = { Text("Cerca movimenti o hashtag...", style = MaterialTheme.typography.bodyMedium.copy(color = Color.White.copy(alpha=0.6f))) },
                                    leadingIcon = { Icon(Icons.Default.Search, null, tint = Color.White.copy(alpha=0.7f)) },
                                    shape = RoundedCornerShape(12.dp), singleLine = true,
                                    colors = OutlinedTextFieldDefaults.colors(focusedContainerColor = Color.White.copy(alpha=0.08f), unfocusedContainerColor = Color.White.copy(alpha=0.08f), focusedBorderColor = MaterialTheme.colorScheme.primary, unfocusedBorderColor = Color.White.copy(alpha = 0.1f))
                                )
                            }
                        }
                    }

                    val isStatsPopupOpen = showAccountSelectorSheet || showLiquiditaImpegnataSheet || activeBreakdownType != null

                    LaunchedEffect(isStatsPopupOpen) {
                        onPopupStateChanged(isStatsPopupOpen)
                    }

                    val statsBlur by animateDpAsState(
                        targetValue = if (isStatsPopupOpen) 12.dp else 0.dp,
                        animationSpec = tween(durationMillis = 300),
                        label = "statsBlur"
                    )

                    LazyColumn(
                        state = listState,
                        modifier = Modifier.weight(1f).padding(horizontal = 16.dp).blur(statsBlur),
                        contentPadding = PaddingValues(top = 8.dp, bottom = 100.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        if (localSearchQuery.isNotBlank()) {
                            item {
                                GlobalSearchResultCard(
                                    query = localSearchQuery,
                                    results = searchResults,
                                    incomeTotal = searchIncomeTotal,
                                    expenseTotal = searchExpenseTotal,
                                    hide = hideBalances,
                                    categoryNameById = categoryNameById,
                                    latestTxs = visibleTxs,
                                    onClose = { localSearchQuery = ""; isSearchActive = false },
                                    onTransactionClick = { onOpenGlobalSearchResult?.invoke(localSearchQuery, it.id) }
                                )
                            }
                        }

                        itemsIndexed(cardOrder) { index: Int, cardType: StatCardType ->
                            Column(modifier = Modifier.fillMaxWidth().animateContentSize()) {
                                val isCurrentMonth = periodOffset == 0
                                when (cardType) {
                                    StatCardType.BALANCES_OVERVIEW -> {
                                        // 🚀 Panoramica / Asset Totali[cite: 3]
                                        BalancesOverviewCard(
                                            currentBalance = saldoContabileCents,
                                            afterFixedBalance = displayBalanceCents,
                                            futureBalance = futureBalanceCents - totalScadenzeMese,
                                            totalSinkingFunds = totalSinkingFundsCents,
                                            totalSogni = totalSogni,
                                            totalSalvadanai = totalSalvadanai,
                                            unpaidFixedCents = totalScadenzeMese,
                                            saldoCongelatoCicloPrecedente = saldoCongelato,
                                            hide = localHideBalances,
                                            saldoVisibileBanca = saldoVisibileBancaPuro,
                                            safetyBufferCents = safetyBufferCents
                                        )
                                    }
                                    StatCardType.CASH_FLOW -> {
                                        // 🚀 Flusso di Cassa Periodo Contabile (Entrate / Uscite / Netto)
                                        CashFlowCard(
                                            income = stats.incomeCents,
                                            expense = stats.expenseCents,
                                            hide = localHideBalances
                                        )
                                    }
                                    StatCardType.LIQUIDITA_IMPEGNATA -> {
                                        // 🚀 Liquidità Impegnata (Tap per Popup Elegante)
                                        LiquiditaImpegnataCard(
                                            liquiditaImpegnata = liquiditaImpegnataCents,
                                            hide = localHideBalances,
                                            onClick = { showLiquiditaImpegnataSheet = true }
                                        )
                                    }
                                    StatCardType.BALANCE -> {
                                        CashFlowCard(
                                            income = stats.incomeCents,
                                            expense = stats.expenseCents,
                                            hide = localHideBalances
                                        )
                                    }
                                    StatCardType.BANK_RECONCILIATION -> {
                                        // 🚀 Strumenti[cite: 3]
                                        BankReconciliationCard(
                                            appCalculatedBalance = saldoVisibileBancaPuro,
                                            adjustmentCents = localBankAdjustment,
                                            sinkingFundsCents = totalSinkingFundsCents,
                                            totalProgrammatiCents = totalProgrammatiCents,
                                            unpaidFixedCents = unpaidFixed,
                                            totalCartelleCents = totalCartelleMese,
                                            totalSogni = totalSogni,
                                            totalPac = totalPac,
                                            totalSalvadanai = totalSalvadanai,
                                            emergencyBalanceCents = emergencyBalanceCents,
                                            onAdjustmentChanged = { newAdj ->
                                                localBankAdjustment = newAdj
                                                settings.putLong("bank_adj_${activeAccountId ?: "global"}", newAdj)
                                                onBankAdjustmentChanged(newAdj)
                                            },
                                            hide = localHideBalances,
                                            onSogniClick = { activeBreakdownType = "sogni" },
                                            onPacClick = { activeBreakdownType = "pac" },
                                            onSalvadanaiClick = { activeBreakdownType = "salvadanai" },
                                            onScadenzeClick = { activeBreakdownType = "scadenze" },
                                            onCartelleClick = { activeBreakdownType = "cartelle" },
                                            onCicliClick = { activeBreakdownType = "cicli" },
                                            onProgrammatiClick = { activeBreakdownType = "programmati" },
                                            onEmergencyClick = { activeBreakdownType = "emergency" }
                                        )
                                    }
                                    StatCardType.HASHTAGS -> if (usedHashtags.isNotEmpty()) HashtagsCard(
                                        tags = usedHashtags,
                                        onTagClick = { tag ->
                                            localSearchQuery = tag
                                            isSearchActive = true
                                            scope.launch { listState.animateScrollToItem(0) }
                                        }
                                    )
                                    StatCardType.FIXED_EXPENSES -> TotalFixedExpensesCard(
                                        totalFixed = totalFixedMonthlyCents,
                                        hide = localHideBalances,
                                        financingItems = financingItems
                                    )
                                    StatCardType.FORECAST -> if (isCurrentMonth) AdvancedForecastCard(
                                        available = displayBalanceCents,
                                        forecast = improvedForecastCents,
                                        unpaidRec = stats.unpaidRecurringCents,
                                        unpaidFin = stats.unpaidFinancingCents,
                                        hide = localHideBalances,
                                        safetyBufferCents = safetyBufferCents
                                    )
                                    StatCardType.KPI -> KpiHealthCard(stats.incomeCents, stats.expenseCents, stats.totalFixedCents, localHideBalances)
                                    StatCardType.PIE -> {
                                        val pieDataPulita = stats.pieData.filter {
                                            val name = it.first.uppercase()
                                            !name.contains("RISPARMI") && !name.contains("SALVADANAIO")
                                        }
                                        CategoryPieCard(data = pieDataPulita, hide = hideBalances) { clicked -> selectedCategoryForDetails = if (selectedCategoryForDetails == clicked) null else clicked }
                                        if (selectedCategoryForDetails != null) {
                                            val dettagliPuliti = visibleTxs.filter {
                                                !it.isDeleted && !isSavingsTx(it) && it.dateEpochDay in periodStart.toEpochDays()..periodEnd.toEpochDays() &&
                                                        (categoryNameById[it.categoryId] ?: "ALTRO").uppercase() == selectedCategoryForDetails!!.uppercase()
                                            }
                                            CategoryDetailsCard(
                                                catName = selectedCategoryForDetails!!,
                                                txs = dettagliPuliti,
                                                recs = recurringItems,
                                                fins = financingItems,
                                                hide = hideBalances,
                                                transactionsList = visibleTxs,
                                                onClose = { selectedCategoryForDetails = null },
                                                onTransactionClick = { onOpenGlobalSearchResult?.invoke("", it.id) }
                                            )
                                        }
                                    }
                                    StatCardType.INSIGHTS -> {
                                        val merchantsPuliti = stats.topMerchants.filter {
                                            val name = it.first.lowercase()
                                            !name.contains("[pb:") && !name.contains("[sav:") && !name.contains("[goal:") && !name.contains("salvadanaio") && !name.contains("arrotondamento")
                                        }
                                        SmartInsightsCard(stats.expenseCents, stats.prevExpenseCents, merchantsPuliti, hideBalances, visibleTxs) { clicked -> selectedMerchantForDetails = if (selectedMerchantForDetails == clicked) null else clicked }
                                        if (selectedMerchantForDetails != null) {
                                            val dettagliPuliti = visibleTxs.filter {
                                                !it.isDeleted && !isSavingsTx(it) && it.dateEpochDay in periodStart.toEpochDays()..periodEnd.toEpochDays() &&
                                                        (it.merchantKey?.uppercase() == selectedMerchantForDetails!!.uppercase() || it.description?.uppercase() == selectedMerchantForDetails!!.uppercase())
                                            }
                                            MerchantDetailsCard(
                                                merchName = selectedMerchantForDetails!!,
                                                txs = dettagliPuliti,
                                                hide = hideBalances,
                                                transactionsList = visibleTxs,
                                                onClose = { selectedMerchantForDetails = null },
                                                onTransactionClick = { onOpenGlobalSearchResult?.invoke("", it.id) }
                                            )
                                        }
                                    }
                                    StatCardType.UPCOMING -> if (isCurrentMonth && (stats.unpaidRecurringList.isNotEmpty() || stats.unpaidFinancingList.isNotEmpty())) UpcomingPaymentsCard(stats.unpaidRecurringList, stats.unpaidFinancingList, hideBalances)
                                    StatCardType.HISTORY -> HistoricalBalanceCard(history = stats.balanceHistory, hide = hideBalances, onPrev = { historyOffset += 1 }, onNext = { if (historyOffset > 0) historyOffset -= 1 })
                                }
                            }
                        }
                    }
                }
            }

            // POPUP DETTAGLI LIQUIDITA'
            if (activeBreakdownType != null) {
                val sogniBreakdown = remember(activeSogniList) { activeSogniList.map { (it.first.name.replace(Regex("\\[(PAC|ACC):.*?\\]"), "").trim().ifBlank { "Obiettivo" }) to it.second } }
                val pacBreakdown = remember(activePacList) { activePacList.map { (it.first.name.replace(Regex("\\[(PAC|ACC):.*?\\]"), "").trim().ifBlank { "Smart PAC" }) to it.second } }
                val salvadanaiBreakdown = remember(activeSalvadanaiList) { activeSalvadanaiList.map { (it.first.name.replace(Regex("\\[(PAC|ACC):.*?\\]"), "").trim().ifBlank { "Salvadanaio" }) to it.second } }
                val scadenzeBreakdown = remember(activeScadenzeMeseList) { activeScadenzeMeseList }
                val cicliBreakdown = remember(activeSinkingFundsList) { activeSinkingFundsList.map { (it.first.name ?: "Ciclo Mensile") to it.second } }
                val programmatiBreakdown = remember(fantasmaList, futuriList) {
                    fantasmaList.map { (it.merchantKey ?: it.description ?: "Movimento Fantasma") to abs(it.amountCents) } +
                            futuriList.map { (it.merchantKey ?: it.description ?: "Movimento Futuro") to abs(it.amountCents) }
                }

                val (title, items) = when (activeBreakdownType) {
                    "sogni" -> "I Miei Sogni" to sogniBreakdown
                    "pac" -> "Smart PAC" to pacBreakdown
                    "salvadanai" -> "Salvadanai Virtuali" to salvadanaiBreakdown
                    "scadenze" -> "Scadenze Mese" to scadenzeBreakdown
                    "cartelle" -> "Cartelle Esattoriali" to activeCartelleList
                    "cicli" -> "Cicli Mensili" to cicliBreakdown
                    "programmati" -> "Movimenti Programmati" to programmatiBreakdown
                    "emergency" -> "Fondo di Emergenza" to listOf("Scudo" to emergencyBalanceCents)
                    else -> "" to emptyList()
                }

                BreakdownDialog(title = title, items = items, hideBalances = hideBalances) {
                    activeBreakdownType = null
                }
            }

            if (showLiquiditaImpegnataSheet) {
                LiquiditaImpegnataBottomSheet(
                    totalImpegnata = liquiditaImpegnataCents,
                    totalSogni = totalSogni,
                    totalPac = totalPac,
                    totalSalvadanai = totalSalvadanai,
                    emergencyBalanceCents = emergencyBalanceCents,
                    unpaidFixedCents = unpaidFixed,
                    totalCartelleCents = totalCartelleMese,
                    sinkingFundsCents = totalSinkingFundsCents,
                    totalProgrammatiCents = totalProgrammatiCents,
                    hideBalances = hideBalances,
                    onDismiss = { showLiquiditaImpegnataSheet = false },
                    onSogniClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "sogni" },
                    onPacClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "pac" },
                    onSalvadanaiClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "salvadanai" },
                    onEmergencyClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "emergency" },
                    onScadenzeClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "scadenze" },
                    onCartelleClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "cartelle" },
                    onCicliClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "cicli" },
                    onProgrammatiClick = { showLiquiditaImpegnataSheet = false; activeBreakdownType = "programmati" }
                )
            }

            if (showAccountSelectorSheet) {
                ModalBottomSheet(
                    onDismissRequest = { showAccountSelectorSheet = false },
                    containerColor = Color.White.copy(alpha = 0.12f),
                    shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp, vertical = 16.dp)
                            .verticalScroll(rememberScrollState())
                    ) {
                        Text(
                            text = "Seleziona Conto per le Statistiche",
                            style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                            color = Color.White
                        )
                        Spacer(Modifier.height(16.dp))

                        Surface(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable {
                                    onSelectActiveAccount(null)
                                    showAccountSelectorSheet = false
                                }
                                .padding(vertical = 12.dp),
                            color = Color.Transparent
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Surface(shape = CircleShape, color = Color(0xFF60A5FA).copy(alpha = 0.20f), modifier = Modifier.size(40.dp)) {
                                    Icon(Icons.Rounded.Language, null, tint = Color(0xFF60A5FA), modifier = Modifier.padding(8.dp))
                                }
                                Spacer(Modifier.width(12.dp))
                                Text(
                                    text = "Tutti i conti (Vista Globale)",
                                    style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold),
                                    color = Color.White
                                )
                            }
                        }
                        HorizontalDivider(color = Color.White.copy(alpha = 0.1f))

                        accounts.filter { !it.isArchived && !it.isDeleted }.forEach { acc ->
                            val cleanName = (stripSystemMarker(acc.name) ?: acc.name).uppercase()
                            Surface(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        onSelectActiveAccount(acc.id)
                                        showAccountSelectorSheet = false
                                    }
                                    .padding(vertical = 12.dp),
                                color = Color.Transparent
                            ) {
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    TxLogo(acc.logoEmoji?.takeIf { it.isNotBlank() }, acc.logoUri?.takeIf { it.isNotBlank() }, acc.name, Modifier.size(40.dp))
                                    Spacer(Modifier.width(12.dp))
                                    Text(
                                        text = cleanName,
                                        style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold),
                                        color = Color.White
                                    )
                                }
                            }
                            HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
                        }
                        Spacer(Modifier.height(32.dp))
                    }
                }
            }
        }
    }
}

@Composable
fun BreakdownDialog(
    title: String,
    items: List<Pair<String, Long>>,
    hideBalances: Boolean,
    onDismiss: () -> Unit
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color.White.copy(alpha = 0.12f),
        title = {
            Text(title, style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFF60A5FA))
        },
        text = {
            if (items.isEmpty()) {
                Text("Nessun elemento presente in questa categoria.", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))
            } else {
                LazyColumn(modifier = Modifier.fillMaxWidth().heightIn(max = 300.dp)) {
                    items(items) { (name, amount) ->
                        Row(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(name.uppercase(), style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.SemiBold), color = Color.White, modifier = Modifier.weight(1f).padding(end = 8.dp), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            ModernMoneyText(cents = amount, hideBalances = hideBalances, color = Color.White, integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                        }
                        HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("CHIUDI", fontWeight = FontWeight.Bold, color = Color.White) }
        }
    )
}

@Composable
private fun buildStatBorder() = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.25f))

// 🚀 RE-DESIGN ASSET TOTALI (Panoramica)[cite: 3]
@Composable
fun BalancesOverviewCard(
    currentBalance: Long,
    afterFixedBalance: Long,
    futureBalance: Long,
    totalSinkingFunds: Long,
    totalSogni: Long,
    totalSalvadanai: Long,
    unpaidFixedCents: Long,
    saldoCongelatoCicloPrecedente: Long,
    hide: Boolean,
    saldoVisibileBanca: Long,
    safetyBufferCents: Long
) {
    val totalAssets = currentBalance + totalSogni + totalSalvadanai
    val liquidPercentage = if (totalAssets > 0) (currentBalance.toFloat() / totalAssets.toFloat()).coerceIn(0f, 1f) else 1f

    Column {
        Text("Panoramica", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White, modifier = Modifier.padding(start = 4.dp, bottom = 12.dp))

        Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.25f))) {
            Column(modifier = Modifier.padding(24.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.Top
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Saldo visibile in banca", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha = 0.7f))
                        Spacer(Modifier.height(4.dp))
                        ModernMoneyText(cents = saldoVisibileBanca, hideBalances = hide, color = Color.White, integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                    }
                    Spacer(Modifier.width(16.dp))
                    Column(horizontalAlignment = Alignment.End) {
                        Text("Disponibile", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha = 0.7f))
                        Spacer(Modifier.height(4.dp))
                        ModernMoneyText(cents = afterFixedBalance, hideBalances = hide, color = getDynamicBalanceColor(afterFixedBalance, safetyBufferCents), integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                    }
                }

                Spacer(Modifier.height(24.dp))

                // Barra Liquidità Azzurra[cite: 3]
                Row(modifier = Modifier.fillMaxWidth().height(8.dp).clip(CircleShape).background(Color.White.copy(alpha = 0.1f))) {
                    Box(modifier = Modifier.fillMaxHeight().fillMaxWidth(liquidPercentage).background(Color(0xFF60A5FA)))
                }

                Spacer(Modifier.height(16.dp))

                Row(verticalAlignment = Alignment.CenterVertically) {
                    Box(modifier = Modifier.size(8.dp).background(Color(0xFF60A5FA), CircleShape))
                    Spacer(Modifier.width(8.dp))
                    Text("Liquidità", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.8f))
                }

                // Dettagli Espandibili (Opzionali/Nascosti dietro tocco)
                var showDetails by remember { mutableStateOf(false) }
                if (showDetails) {
                    Spacer(Modifier.height(24.dp))
                    HorizontalDivider(color = Color.White.copy(alpha=0.1f))
                    Spacer(Modifier.height(16.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Disponibilità Reale", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f))
                        ModernMoneyText(cents = afterFixedBalance, hideBalances = hide, color = getDynamicBalanceColor(afterFixedBalance, safetyBufferCents), integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                    }
                    Spacer(Modifier.height(8.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Proiezione Futura", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f))
                        ModernMoneyText(cents = futureBalance, hideBalances = hide, color = getDynamicBalanceColor(futureBalance, safetyBufferCents), integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                    }
                    Spacer(Modifier.height(8.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                        Text("Avanzo Ciclo Prec.", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f))
                        ModernMoneyText(cents = saldoCongelatoCicloPrecedente, hideBalances = hide, color = Color(0xFF60A5FA), integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                    }
                }

                TextButton(onClick = { showDetails = !showDetails }, modifier = Modifier.align(Alignment.CenterHorizontally).padding(top = 8.dp)) {
                    Text(if(showDetails) "Meno dettagli" else "Vedi dettagli liquidità", color = Color.White.copy(alpha = 0.5f), fontSize = 12.sp)
                }
            }
        }
    }
}

// 🚀 CARD FLUSSO DI CASSA (PERIODO CONTABILE)
@Composable
fun CashFlowCard(income: Long, expense: Long, hide: Boolean) {
    val absIncome = abs(income)
    val absExpense = abs(expense)
    val net = absIncome - absExpense

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(28.dp),
        color = Color.White.copy(alpha = 0.12f),
        border = buildStatBorder()
    ) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Flusso di cassa (Periodo)", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha = 0.7f))
            Spacer(Modifier.height(14.dp))

            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                // Box Entrate
                Surface(
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(20.dp),
                    color = Color.White.copy(alpha = 0.08f)
                ) {
                    Column(modifier = Modifier.padding(14.dp)) {
                        Text("Entrate", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.6f))
                        Spacer(Modifier.height(4.dp))
                        ModernMoneyText(cents = absIncome, hideBalances = hide, sign = "+", color = Color(0xFF10B981), integerFontSize = 20.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                    }
                }

                // Box Uscite
                Surface(
                    modifier = Modifier.weight(1f),
                    shape = RoundedCornerShape(20.dp),
                    color = Color.White.copy(alpha = 0.08f)
                ) {
                    Column(modifier = Modifier.padding(14.dp)) {
                        Text("Uscite", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.6f))
                        Spacer(Modifier.height(4.dp))
                        ModernMoneyText(cents = absExpense, hideBalances = hide, sign = "-", color = MaterialTheme.colorScheme.error, integerFontSize = 20.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                    }
                }
            }

            Spacer(Modifier.height(14.dp))
            HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
            Spacer(Modifier.height(10.dp))

            // Riga Flusso Netto
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("Flusso netto periodo", style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
                ModernMoneyText(
                    cents = abs(net),
                    hideBalances = hide,
                    sign = if (net >= 0) "+" else "-",
                    color = if (net >= 0) Color(0xFF10B981) else MaterialTheme.colorScheme.error,
                    integerFontSize = 18.sp,
                    decimalFontSize = 12.sp,
                    symbolFontSize = 12.sp
                )
            }
        }
    }
}

// 🚀 CARD LIQUIDITA' IMPEGNATA
@Composable
fun LiquiditaImpegnataCard(
    liquiditaImpegnata: Long,
    hide: Boolean,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth().clickable { onClick() },
        shape = RoundedCornerShape(28.dp),
        color = Color.White.copy(alpha = 0.12f),
        border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.25f))
    ) {
        Row(
            modifier = Modifier.padding(20.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Rounded.Lock, contentDescription = null, tint = Color(0xFFF59E0B), modifier = Modifier.size(18.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Liquidità impegnata", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                }
                Spacer(Modifier.height(6.dp))
                ModernMoneyText(
                    cents = liquiditaImpegnata,
                    hideBalances = hide,
                    color = Color(0xFFF59E0B),
                    integerFontSize = 24.sp,
                    decimalFontSize = 14.sp,
                    symbolFontSize = 14.sp
                )
                Spacer(Modifier.height(4.dp))
                Text("Tocca per vedere il dettaglio completo", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.5f))
            }
            Icon(Icons.Default.ChevronRight, contentDescription = "Dettagli", tint = Color.White.copy(alpha = 0.5f), modifier = Modifier.size(20.dp))
        }
    }
}

// 🚀 RE-DESIGN STRUMENTI E ALLINEAMENTO[cite: 3]
@Composable
fun BankReconciliationCard(
    appCalculatedBalance: Long,
    adjustmentCents: Long,
    sinkingFundsCents: Long,
    totalProgrammatiCents: Long,
    unpaidFixedCents: Long,
    totalCartelleCents: Long,
    totalSogni: Long,
    totalPac: Long,
    totalSalvadanai: Long,
    emergencyBalanceCents: Long,
    onAdjustmentChanged: (Long) -> Unit,
    hide: Boolean,
    onSogniClick: () -> Unit,
    onPacClick: () -> Unit,
    onSalvadanaiClick: () -> Unit,
    onScadenzeClick: () -> Unit,
    onCartelleClick: () -> Unit,
    onCicliClick: () -> Unit,
    onProgrammatiClick: () -> Unit,
    onEmergencyClick: () -> Unit
) {
    var showAbsoluteDialog by remember { mutableStateOf(false) }
    var showDeltaDialog by remember { mutableStateOf(false) }

    val realBankBalance = appCalculatedBalance + adjustmentCents
    val liquiditaImpegnata = totalSogni + totalPac + totalSalvadanai + sinkingFundsCents + unpaidFixedCents + totalCartelleCents + totalProgrammatiCents + emergencyBalanceCents
    val fondiLiberi = realBankBalance - liquiditaImpegnata

    Column {
        Text("Strumenti", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White, modifier = Modifier.padding(start = 4.dp, bottom = 12.dp))

        Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
            Column(modifier = Modifier.padding(vertical = 12.dp)) {

                // Item 1: Allinea Saldo (Mostra SEMPRE e SOLO il saldo visibile in banca puro)
                Row(modifier = Modifier.fillMaxWidth().clickable { showAbsoluteDialog = true }.padding(horizontal = 24.dp, vertical = 16.dp), verticalAlignment = Alignment.CenterVertically) {
                    Surface(shape = CircleShape, color = Color.White.copy(alpha = 0.1f), modifier = Modifier.size(36.dp)) { Icon(Icons.Rounded.AccountBalance, null, tint = Color.White, modifier = Modifier.padding(8.dp)) }
                    Spacer(Modifier.width(16.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Allinea saldo banca", style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
                        Text(if(hide) "•••" else formatKmpEuro(appCalculatedBalance), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.6f))
                    }
                    Icon(Icons.Default.ChevronRight, null, tint = Color.White.copy(alpha=0.5f))
                }
                HorizontalDivider(color = Color.White.copy(alpha=0.05f), modifier = Modifier.padding(horizontal = 24.dp))

                // Item 2: Regola Extra
                Row(modifier = Modifier.fillMaxWidth().clickable { showDeltaDialog = true }.padding(horizontal = 24.dp, vertical = 16.dp), verticalAlignment = Alignment.CenterVertically) {
                    Surface(shape = CircleShape, color = Color.White.copy(alpha = 0.1f), modifier = Modifier.size(36.dp)) { Icon(Icons.Rounded.CompareArrows, null, tint = Color.White, modifier = Modifier.padding(8.dp)) }
                    Spacer(Modifier.width(16.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Regola fondi extra", style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
                        if (adjustmentCents != 0L && !hide) Text(if (adjustmentCents > 0) "+ ${formatKmpEuro(adjustmentCents)}" else formatKmpEuro(adjustmentCents), style = MaterialTheme.typography.bodySmall, color = if (adjustmentCents > 0) Color(0xFF10B981) else MaterialTheme.colorScheme.error)
                    }
                    Icon(Icons.Default.ChevronRight, null, tint = Color.White.copy(alpha=0.5f))
                }

                // Item 3: Nuova riga con Saldo visibile in banca + fondi aggiunti o detratti (mostrato se sono presenti fondi extra)
                if (adjustmentCents != 0L) {
                    HorizontalDivider(color = Color.White.copy(alpha=0.05f), modifier = Modifier.padding(horizontal = 24.dp))
                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Surface(shape = CircleShape, color = Color(0xFF10B981).copy(alpha = 0.15f), modifier = Modifier.size(36.dp)) { Icon(Icons.Rounded.AccountBalanceWallet, null, tint = Color(0xFF10B981), modifier = Modifier.padding(8.dp)) }
                        Spacer(Modifier.width(16.dp))
                        Column(modifier = Modifier.weight(1f)) {
                            Text("Saldo visibile + Fondi extra", style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = Color.White)
                            Text(if(hide) "•••" else formatKmpEuro(appCalculatedBalance + adjustmentCents), style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold), color = Color(0xFF10B981))
                        }
                    }
                }
            }
        }
    }

    if (showAbsoluteDialog) {
        var inputText by remember { mutableStateOf("") }
        AlertDialog(
            onDismissRequest = { showAbsoluteDialog = false },
            containerColor = Color.White.copy(alpha = 0.12f),
            shape = RoundedCornerShape(28.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        Icons.Rounded.AccountBalance,
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text("Allinea Saldo Banca", color = Color.White, style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold))
                }
            },
            text = {
                Column {
                    Text("Inserisci il saldo esatto che vedi ORA nella tua app della banca.", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                    Spacer(Modifier.height(16.dp))
                    OutlinedTextField(
                        value = inputText,
                        onValueChange = { if (it.all { char -> char.isDigit() || char == ',' || char == '-' || char == '.' }) inputText = it },
                        label = { Text("Saldo Reale (€)", color = Color.White.copy(alpha = 0.7f)) },
                        placeholder = { Text(formatKmpEuro(appCalculatedBalance).replace(" €", ""), color = Color.White.copy(alpha = 0.4f)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedTextColor = Color.White,
                            unfocusedTextColor = Color.White,
                            focusedContainerColor = Color.White.copy(alpha = 0.08f),
                            unfocusedContainerColor = Color.White.copy(alpha = 0.08f),
                            focusedBorderColor = Color(0xFF60A5FA),
                            unfocusedBorderColor = Color.White.copy(alpha = 0.2f),
                            focusedLabelColor = Color(0xFF60A5FA),
                            unfocusedLabelColor = Color.White.copy(alpha = 0.7f)
                        )
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (inputText.isNotBlank()) {
                            val clean = inputText.replace(",", ".")
                            val newRealCents = ((clean.toDoubleOrNull() ?: 0.0) * 100).toLong()
                            onAdjustmentChanged(newRealCents - appCalculatedBalance)
                        }
                        showAbsoluteDialog = false
                    },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                ) { Text("SALVA", fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                TextButton(onClick = { showAbsoluteDialog = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }

    if (showDeltaDialog) {
        var deltaText by remember { mutableStateOf("") }
        var isAdding by remember { mutableStateOf(true) }
        AlertDialog(
            onDismissRequest = { showDeltaDialog = false },
            containerColor = Color.White.copy(alpha = 0.12f),
            shape = RoundedCornerShape(28.dp),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        Icons.Rounded.CompareArrows,
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text("Regola Fondi Extra", color = Color.White, style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold))
                }
            },
            text = {
                Column {
                    Text("Aggiungi o rimuovi fondi dal saldo reale senza creare movimenti.", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                    Spacer(Modifier.height(16.dp))
                    Row(modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(12.dp)).background(Color.Black.copy(alpha = 0.25f))) {
                        Box(modifier = Modifier.weight(1f).clickable { isAdding = true }.background(if (isAdding) Color(0xFF10B981) else Color.Transparent).padding(vertical = 12.dp), contentAlignment = Alignment.Center) { Text("AGGIUNGI (+)", color = if (isAdding) Color.White else Color.White.copy(alpha=0.5f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall) }
                        Box(modifier = Modifier.weight(1f).clickable { isAdding = false }.background(if (!isAdding) MaterialTheme.colorScheme.error else Color.Transparent).padding(vertical = 12.dp), contentAlignment = Alignment.Center) { Text("RIMUOVI (-)", color = if (!isAdding) Color.White else Color.White.copy(alpha=0.5f), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.labelSmall) }
                    }
                    Spacer(Modifier.height(16.dp))
                    OutlinedTextField(
                        value = deltaText,
                        onValueChange = { if (it.all { char -> char.isDigit() || char == ',' || char == '.' }) deltaText = it },
                        label = { Text("Importo (€)", color = Color(0xFFA0A0A0)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(14.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedTextColor = Color.White,
                            unfocusedTextColor = Color.White,
                            focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                            unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                            focusedBorderColor = Color(0xFF60A5FA),
                            unfocusedBorderColor = Color.White.copy(alpha = 0.2f),
                            focusedLabelColor = Color(0xFF60A5FA),
                            unfocusedLabelColor = Color(0xFFA0A0A0)
                        )
                    )
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (deltaText.isNotBlank()) {
                            val clean = deltaText.replace(",", ".")
                            val deltaCents = ((clean.toDoubleOrNull() ?: 0.0) * 100).toLong()
                            val finalDelta = if (isAdding) deltaCents else -deltaCents
                            onAdjustmentChanged(adjustmentCents + finalDelta)
                        }
                        showDeltaDialog = false
                    },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                ) { Text("REGOLA", fontWeight = FontWeight.Bold) }
            },
            dismissButton = {
                TextButton(onClick = { showDeltaDialog = false }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }
}

@Composable
fun HashtagsCard(tags: List<String>, onTagClick: (String) -> Unit) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Progetti & Eventi", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
            Spacer(Modifier.height(16.dp))
            LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                items(tags) { tag ->
                    Surface(modifier = Modifier.clickable { onTagClick(tag) }, shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.08f)) {
                        Text(tag, modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp), style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                    }
                }
            }
        }
    }
}

@Composable
fun TotalFixedExpensesCard(totalFixed: Long, hide: Boolean, financingItems: List<FinancingEntity>) {
    var isExpanded by remember { mutableStateOf(false) }
    Surface(modifier = Modifier.fillMaxWidth().clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = { isExpanded = !isExpanded }), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp).animateContentSize()) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                Column {
                    Text("Spese Fisse Mensili", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
                    Spacer(Modifier.height(8.dp))
                    ModernMoneyText(cents = totalFixed, hideBalances = hide, color = Color.White, integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                }
                Icon(imageVector = if (isExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore, contentDescription = "Espandi", tint = Color.White.copy(alpha=0.5f))
            }
            if (isExpanded) {
                Spacer(Modifier.height(24.dp))
                HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
                Spacer(Modifier.height(16.dp))
                Text("LIBERAZIONE DEBITI", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha=0.7f))
                Spacer(Modifier.height(16.dp))
                val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
                val upcomingDrops: List<Pair<LocalDate, List<FinancingEntity>>> = remember(financingItems) {
                    val dropsMap = mutableMapOf<LocalDate, MutableList<FinancingEntity>>()
                    financingItems.forEach { fin ->
                        if (!fin.isDeleted && !fin.isClosed) {
                            try {
                                val endDate = LocalDate.fromEpochDays(fin.endEpochDay.toInt())
                                if (endDate > today) {
                                    val dropMonth = LocalDate(endDate.year, endDate.monthNumber, 1)
                                    if (dropsMap[dropMonth] == null) dropsMap[dropMonth] = mutableListOf()
                                    dropsMap[dropMonth]!!.add(fin)
                                }
                            } catch (e: Exception) {}
                        }
                    }
                    dropsMap.map { Pair(it.key, it.value.toList()) }.sortedBy { it.first }
                }

                if (upcomingDrops.isEmpty()) Text("Nessun debito in scadenza a breve termine.", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.5f))
                else {
                    var runningTotal = totalFixed
                    Column(modifier = Modifier.fillMaxWidth()) {
                        upcomingDrops.forEach { dropPair ->
                            val dropDate = dropPair.first
                            val finsEnding = dropPair.second
                            val dropAmount = finsEnding.sumOf { it.installmentAmountCents + it.commissionCents }
                            runningTotal -= dropAmount
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 12.dp), verticalAlignment = Alignment.Top) {
                                Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.width(32.dp)) {
                                    Surface(shape = CircleShape, color = Color(0xFF10B981), modifier = Modifier.size(12.dp)) {}
                                    Box(modifier = Modifier.width(2.dp).height(40.dp).background(Color.White.copy(alpha = 0.1f)))
                                }
                                Column(modifier = Modifier.weight(1f).padding(start = 8.dp)) {
                                    Text("${getMonthName(dropDate.monthNumber)} ${dropDate.year}".uppercase(), style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.ExtraBold), color = Color.White)
                                    finsEnding.forEach { fin -> Row(modifier = Modifier.fillMaxWidth().padding(top = 4.dp), horizontalArrangement = Arrangement.SpaceBetween) { Text("Fine ${fin.name}", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.6f)); ModernMoneyText(cents = fin.installmentAmountCents + fin.commissionCents, hideBalances = hide, sign = "-", color = Color(0xFF10B981), integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp) } }
                                    Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) { Text("Nuovo carico mensile", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha=0.8f)); ModernMoneyText(cents = runningTotal.coerceAtLeast(0L), hideBalances = hide, color = Color.White, integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun AdvancedForecastCard(available: Long, forecast: Long, unpaidRec: Long, unpaidFin: Long, hide: Boolean, safetyBufferCents: Long) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color(0xFF60A5FA).copy(alpha = 0.15f), border = BorderStroke(1.dp, Color(0xFF60A5FA).copy(alpha = 0.3f))) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Sfera di cristallo", style = MaterialTheme.typography.labelMedium, color = Color(0xFF60A5FA))
            Spacer(Modifier.height(8.dp))
            ModernMoneyText(cents = forecast, hideBalances = hide, color = getDynamicBalanceColor(forecast, safetyBufferCents), integerFontSize = 28.sp, decimalFontSize = 16.sp, symbolFontSize = 16.sp)
            Spacer(Modifier.height(4.dp))
            Text("Saldo previsto prima del prossimo stipendio", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.5f))
        }
    }
}

@Composable
fun KpiHealthCard(income: Long, expense: Long, totalFixed: Long, hide: Boolean) {
    val savingsRate = if (income > 0) ((income - expense).toFloat() / income.toFloat() * 100).toInt() else 0
    val fixedIncidence = if (income > 0) ((totalFixed).toFloat() / income.toFloat() * 100).toInt() else 0
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Row(modifier = Modifier.padding(20.dp).fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Column(modifier = Modifier.weight(1f)) {
                Text("Tasso Risparmio", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha=0.7f))
                Spacer(Modifier.height(8.dp))
                Text(if(hide) "••%" else "$savingsRate%", style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold), color = if (savingsRate >= 20) Color(0xFF10B981) else Color.White)
            }
            Column(modifier = Modifier.weight(1f), horizontalAlignment = Alignment.End) {
                Text("Incidenza Fisse", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha=0.7f))
                Spacer(Modifier.height(8.dp))
                Text(if(hide) "••%" else "$fixedIncidence%", style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold), color = if (fixedIncidence <= 50) Color(0xFF10B981) else MaterialTheme.colorScheme.error)
            }
        }
    }
}

@Composable
fun CategoryPieCard(data: List<Pair<String, Long>>, hide: Boolean, onCategoryClick: (String) -> Unit) {
    val totalExpense = data.sumOf { abs(it.second) }
    val colors = listOf(Color(0xFF6200EE), Color(0xFF03DAC6), Color(0xFFFF0266), Color(0xFFFFDE03), Color(0xFF4CAF50))
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Ripartizione Spese", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
            Spacer(Modifier.height(20.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(modifier = Modifier.size(100.dp), contentAlignment = Alignment.Center) {
                    Canvas(modifier = Modifier.size(100.dp)) {
                        if (totalExpense > 0) {
                            var start = -90f
                            data.forEachIndexed { i, p ->
                                val angle = (abs(p.second).toFloat() / totalExpense.toFloat()) * 360f
                                drawArc(colors[i % colors.size], start, angle, false, style = Stroke(width = 24f, cap = StrokeCap.Round))
                                start += angle
                            }
                        } else drawArc(Color.White.copy(alpha=0.1f), 0f, 360f, false, style = Stroke(width = 24f, cap = StrokeCap.Round))
                    }
                }
                Spacer(Modifier.width(24.dp))
                Column {
                    data.take(5).forEachIndexed { i, p ->
                        val displayName = p.first.takeIf { it.isNotBlank() } ?: "ALTRO"
                        Row(modifier = Modifier.clickable { onCategoryClick(displayName) }.padding(vertical = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(8.dp).background(colors[i % colors.size], CircleShape))
                            Spacer(Modifier.width(8.dp))
                            Text(displayName, style = MaterialTheme.typography.labelSmall, color = Color.White, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                            if (!hide) ModernMoneyText(cents = abs(p.second), hideBalances = false, color = Color.White.copy(alpha=0.7f), integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun SmartInsightsCard(current: Long, prev: Long, merchants: List<Pair<String, Long>>, hide: Boolean, transactions: List<TransactionEntity>, onMerchantClick: (String) -> Unit) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Top Merchants", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
            Spacer(Modifier.height(16.dp))
            merchants.forEach { m ->
                Row(modifier = Modifier.fillMaxWidth().clickable { onMerchantClick(m.first) }.padding(vertical = 6.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(cleanTextForUi(m.first, transactions), style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold), color = Color.White, maxLines = 1, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f).padding(end = 8.dp))
                    ModernMoneyText(cents = abs(m.second), hideBalances = hide, color = Color.White.copy(alpha=0.7f), integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                }
            }
        }
    }
}

@Composable
fun UpcomingPaymentsCard(recurring: List<RecurringExpenseEntity>, financing: List<FinancingEntity>, hide: Boolean) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Text("Prossimi Movimenti", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
            Spacer(Modifier.height(16.dp))
            val items = (recurring.map { (it.name) to it.amountCents } + financing.map { (it.name ?: "Rata") to (it.installmentAmountCents + (it.commissionCents ?: 0L)) }).take(5)
            items.forEach { (name, amount) ->
                Row(modifier = Modifier.fillMaxWidth().padding(vertical = 6.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(name ?: "Sconosciuto", style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold), color = Color.White)
                    ModernMoneyText(cents = amount, hideBalances = hide, color = Color.White.copy(alpha=0.7f), integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                }
            }
        }
    }
}

@Composable
fun HistoricalBalanceCard(history: List<Pair<String, Long>>, hide: Boolean, onPrev: () -> Unit, onNext: () -> Unit) {
    if (history.isEmpty()) return
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha=0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("Storico Patrimonio", style = MaterialTheme.typography.labelMedium, color = Color.White.copy(alpha=0.7f))
                Row {
                    IconButton(onClick = onPrev, modifier = Modifier.size(24.dp)) { Icon(Icons.Default.ChevronLeft, null, tint = Color.White.copy(alpha=0.5f)) }
                    Spacer(Modifier.width(8.dp))
                    IconButton(onClick = onNext, modifier = Modifier.size(24.dp)) { Icon(Icons.Default.ChevronRight, null, tint = Color.White.copy(alpha=0.5f)) }
                }
            }
            Spacer(Modifier.height(24.dp))
            val maxVal = maxOf(history.maxOfOrNull { it.second } ?: 1L, 1L).toFloat()
            Row(modifier = Modifier.fillMaxWidth().height(160.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                history.forEachIndexed { i, (name, amount) ->
                    val h = (amount / maxVal).coerceIn(0.05f, 1f)
                    Column(modifier = Modifier.weight(1f).fillMaxHeight(), horizontalAlignment = Alignment.CenterHorizontally) {
                        Spacer(modifier = Modifier.weight((1f - h).coerceAtLeast(0.01f)))
                        Text(if (hide) "••" else formatShortNumber(amount / 100), style = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp), color = Color.White.copy(alpha=0.7f))
                        Spacer(Modifier.height(8.dp))
                        Box(modifier = Modifier.fillMaxWidth(0.6f).weight((h).coerceAtLeast(0.01f)).background(Color(0xFF60A5FA), RoundedCornerShape(topStart = 4.dp, topEnd = 4.dp)))
                        Spacer(Modifier.height(8.dp))
                        Text(name, style = MaterialTheme.typography.labelSmall.copy(fontSize = 9.sp), color = Color.White.copy(alpha=0.5f), maxLines = 1)
                    }
                }
            }
        }
    }
}

@Composable
fun CategoryDetailsCard(catName: String, txs: List<TransactionEntity>, recs: List<RecurringExpenseEntity>, fins: List<FinancingEntity>, hide: Boolean, transactionsList: List<TransactionEntity>, onClose: () -> Unit, onTransactionClick: ((TransactionEntity) -> Unit)? = null) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha = 0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                val displayCat = catName.takeIf { it.isNotBlank() } ?: "Altro"
                Text(displayCat, style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                Icon(Icons.Rounded.Close, null, tint = Color.White.copy(alpha=0.5f), modifier = Modifier.clickable(onClick = onClose))
            }
            Spacer(Modifier.height(16.dp))
            txs.forEach { tx ->
                Row(
                    modifier = Modifier.fillMaxWidth().clickable(enabled = onTransactionClick != null) { onTransactionClick?.invoke(tx) }.padding(vertical = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        val finalName = tx.merchantKey?.takeIf { it.isNotBlank() } ?: tx.description?.takeIf { it.isNotBlank() } ?: "Operazione"
                        Text(cleanTextForUi(finalName, transactionsList), style = MaterialTheme.typography.bodyMedium, color = Color.White)
                        Text(formatKmpDateShort(tx.dateEpochDay), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.5f))
                    }
                    ModernMoneyText(cents = tx.amountCents, hideBalances = hide, color = Color.White.copy(alpha=0.8f), integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                }
            }
        }
    }
}

@Composable
fun MerchantDetailsCard(merchName: String, txs: List<TransactionEntity>, hide: Boolean, transactionsList: List<TransactionEntity>, onClose: () -> Unit, onTransactionClick: ((TransactionEntity) -> Unit)? = null) {
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color.White.copy(alpha = 0.12f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(cleanTextForUi(merchName, transactionsList), style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White, modifier = Modifier.weight(1f), maxLines = 1, overflow = TextOverflow.Ellipsis)
                Icon(Icons.Rounded.Close, null, tint = Color.White.copy(alpha=0.5f), modifier = Modifier.clickable(onClick = onClose).padding(start = 8.dp))
            }
            Spacer(Modifier.height(16.dp))
            txs.forEach { tx ->
                Row(
                    modifier = Modifier.fillMaxWidth().clickable(enabled = onTransactionClick != null) { onTransactionClick?.invoke(tx) }.padding(vertical = 8.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(formatKmpDateShort(tx.dateEpochDay), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.7f))
                    ModernMoneyText(cents = tx.amountCents, hideBalances = hide, color = Color.White.copy(alpha=0.8f), integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                }
            }
        }
    }
}

@Composable
fun GlobalSearchResultCard(
    query: String,
    results: List<TransactionEntity>,
    incomeTotal: Long,
    expenseTotal: Long,
    hide: Boolean,
    categoryNameById: Map<String, String>,
    latestTxs: List<TransactionEntity>,
    onClose: () -> Unit,
    onTransactionClick: (TransactionEntity) -> Unit
) {
    val netBalance = incomeTotal - expenseTotal
    Surface(modifier = Modifier.fillMaxWidth(), shape = RoundedCornerShape(28.dp), color = Color(0xFF60A5FA).copy(alpha = 0.15f), border = buildStatBorder()) {
        Column(modifier = Modifier.padding(20.dp)) {
            Row(horizontalArrangement = Arrangement.SpaceBetween, modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.Top) {
                Column(modifier = Modifier.weight(1f)) {
                    Text("Risultati per: ${query.uppercase()}", style = MaterialTheme.typography.labelMedium, color = Color(0xFF60A5FA))
                    Spacer(Modifier.height(16.dp))

                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        if (incomeTotal > 0) {
                            Column {
                                Text("ENTRATE", style = MaterialTheme.typography.labelSmall, color = Color(0xFF10B981))
                                ModernMoneyText(cents = incomeTotal, hideBalances = hide, color = Color.White, integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                            }
                        }
                        if (expenseTotal > 0) {
                            Column {
                                Text("USCITE", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.error)
                                ModernMoneyText(cents = expenseTotal, hideBalances = hide, color = Color.White, integerFontSize = 14.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                            }
                        }
                    }
                    Spacer(Modifier.height(12.dp))
                    Row(verticalAlignment = Alignment.Bottom) {
                        Text(if(hide) "SALDO: " else "SALDO NETTO: ", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.ExtraBold), color = if (netBalance >= 0) Color(0xFF10B981) else MaterialTheme.colorScheme.error, modifier = Modifier.padding(bottom = 2.dp))
                        ModernMoneyText(cents = netBalance, hideBalances = hide, color = if (netBalance >= 0) Color(0xFF10B981) else MaterialTheme.colorScheme.error, integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                    }
                }
                Icon(Icons.Default.Close, null, tint = Color.White.copy(alpha=0.5f), modifier = Modifier.clickable(onClick = onClose).padding(4.dp))
            }

            Spacer(Modifier.height(16.dp))
            HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
            Spacer(Modifier.height(8.dp))

            results.take(15).forEach { tx ->
                Row(modifier = Modifier.fillMaxWidth().clickable { onTransactionClick(tx) }.padding(vertical = 8.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Column(modifier = Modifier.weight(1f)) {
                        val finalName = tx.merchantKey?.takeIf { it.isNotBlank() } ?: tx.description?.takeIf { it.isNotBlank() } ?: "Operazione"
                        Text(cleanTextForUi(finalName, latestTxs).uppercase(), style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold), color = Color.White, maxLines = 1, overflow = TextOverflow.Ellipsis)
                        Text("${formatKmpDateShort(tx.dateEpochDay)} • ${categoryNameById[tx.categoryId] ?: "ALTRO"}", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha=0.5f))
                    }
                    ModernMoneyText(cents = tx.amountCents, hideBalances = hide, color = if (tx.type == "INCOME") Color(0xFF10B981) else Color.White, integerFontSize = 14.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LiquiditaImpegnataBottomSheet(
    totalImpegnata: Long,
    totalSogni: Long,
    totalPac: Long,
    totalSalvadanai: Long,
    emergencyBalanceCents: Long,
    unpaidFixedCents: Long,
    totalCartelleCents: Long,
    sinkingFundsCents: Long,
    totalProgrammatiCents: Long,
    hideBalances: Boolean,
    onDismiss: () -> Unit,
    onSogniClick: () -> Unit,
    onPacClick: () -> Unit,
    onSalvadanaiClick: () -> Unit,
    onEmergencyClick: () -> Unit,
    onScadenzeClick: () -> Unit,
    onCartelleClick: () -> Unit,
    onCicliClick: () -> Unit,
    onProgrammatiClick: () -> Unit
) {
    ModalBottomSheet(
        onDismissRequest = onDismiss,
        containerColor = Color(0xFF1E1E1E).copy(alpha = 0.8f),
        scrimColor = Color.Black.copy(alpha = 0.6f),
        shape = RoundedCornerShape(topStart = 28.dp, topEnd = 28.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp, vertical = 16.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text("Liquidità Impegnata", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Black), color = Color.White)
                    Text("Totale impegni calcolati nel periodo", style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.6f))
                }
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Default.Close, contentDescription = "Chiudi", tint = Color.White.copy(alpha = 0.7f))
                }
            }

            Spacer(Modifier.height(16.dp))

            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                color = Color(0xFFF59E0B).copy(alpha = 0.15f),
                border = BorderStroke(1.dp, Color(0xFFF59E0B).copy(alpha = 0.3f))
            ) {
                Row(
                    modifier = Modifier.padding(16.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Rounded.Lock, contentDescription = null, tint = Color(0xFFF59E0B), modifier = Modifier.size(24.dp))
                        Spacer(Modifier.width(12.dp))
                        Text("Totale Impegnato", style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold), color = Color.White)
                    }
                    ModernMoneyText(
                        cents = totalImpegnata,
                        hideBalances = hideBalances,
                        color = Color(0xFFF59E0B),
                        integerFontSize = 20.sp,
                        decimalFontSize = 14.sp,
                        symbolFontSize = 14.sp
                    )
                }
            }

            Spacer(Modifier.height(20.dp))
            HorizontalDivider(color = Color.White.copy(alpha = 0.1f))
            Spacer(Modifier.height(12.dp))

            LazyColumn(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (totalSogni > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🎯",
                            title = "I Miei Sogni",
                            subtitle = "Obiettivi di risparmio attivi",
                            amount = totalSogni,
                            hide = hideBalances,
                            onClick = { onDismiss(); onSogniClick() }
                        )
                    }
                }
                if (totalPac > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🤖",
                            title = "Smart PAC",
                            subtitle = "Piani di accumulo attivi",
                            amount = totalPac,
                            hide = hideBalances,
                            onClick = { onDismiss(); onPacClick() }
                        )
                    }
                }
                if (totalSalvadanai > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🪙",
                            title = "Salvadanai Virtuali",
                            subtitle = "Fondi accantonati nei salvadanai",
                            amount = totalSalvadanai,
                            hide = hideBalances,
                            onClick = { onDismiss(); onSalvadanaiClick() }
                        )
                    }
                }
                if (emergencyBalanceCents > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🛡️",
                            title = "Scudo / Fondo Emergenza",
                            subtitle = "Riserva di sicurezza immobilizzata",
                            amount = emergencyBalanceCents,
                            hide = hideBalances,
                            onClick = { onDismiss(); onEmergencyClick() }
                        )
                    }
                }
                if (unpaidFixedCents > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "📅",
                            title = "Scadenze Fisse Mese",
                            subtitle = "Spese fisse e rate da saldare",
                            amount = unpaidFixedCents,
                            hide = hideBalances,
                            onClick = { onDismiss(); onScadenzeClick() }
                        )
                    }
                }
                if (totalCartelleCents > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🏛️",
                            title = "Cartelle / Tasse",
                            subtitle = "Rate di cartelle esattoriali",
                            amount = totalCartelleCents,
                            hide = hideBalances,
                            onClick = { onDismiss(); onCartelleClick() }
                        )
                    }
                }
                if (sinkingFundsCents > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "🔄",
                            title = "Cicli Mensili",
                            subtitle = "Fondi per spese cicliche",
                            amount = sinkingFundsCents,
                            hide = hideBalances,
                            onClick = { onDismiss(); onCicliClick() }
                        )
                    }
                }
                if (totalProgrammatiCents > 0L) {
                    item {
                        ImpegnoItemRow(
                            emoji = "👻",
                            title = "Movimenti Programmati",
                            subtitle = "Spese fantasma e future programmate",
                            amount = totalProgrammatiCents,
                            hide = hideBalances,
                            onClick = { onDismiss(); onProgrammatiClick() }
                        )
                    }
                }
            }
            Spacer(Modifier.height(24.dp))
        }
    }
}

@Composable
private fun ImpegnoItemRow(
    emoji: String,
    title: String,
    subtitle: String,
    amount: Long,
    hide: Boolean,
    onClick: () -> Unit
) {
    Surface(
        modifier = Modifier.fillMaxWidth().clip(RoundedCornerShape(14.dp)).clickable { onClick() },
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.12f)),
        shape = RoundedCornerShape(14.dp)
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
                Text(emoji, fontSize = 22.sp)
                Spacer(Modifier.width(12.dp))
                Column {
                    Text(title, style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                    Text(subtitle, style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.6f))
                }
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                ModernMoneyText(
                    cents = amount,
                    hideBalances = hide,
                    color = Color.White,
                    integerFontSize = 15.sp,
                    decimalFontSize = 11.sp,
                    symbolFontSize = 11.sp
                )
                Spacer(Modifier.width(6.dp))
                Icon(Icons.Default.ChevronRight, contentDescription = null, tint = Color.White.copy(alpha = 0.5f), modifier = Modifier.size(18.dp))
            }
        }
    }
}